import { AxiosError } from 'axios';
import Geocode from 'react-geocode';

import { Activity, AdditionalCharges } from '../../types/Activity';
import {
  ACTIVITY_AVAILABLE_DAY_WEEKS,
  CANCELLATION_POLICY_OPTIONS,
} from '../constants/activities';

import { escapeQuote, removeHTML } from './common';

async function getGeoAddressFromString(string) {
  const foundPosition = await Geocode.fromAddress(string);
  if (!foundPosition) return '';
  const positionResult = foundPosition.results[0];
  return {
    lat: positionResult.geometry.location.lat,
    lng: positionResult.geometry.location.lng,
    address: positionResult.formatted_address,
    addressComponents: positionResult.address_components,
  };
}

export function getAvailabilityType(hours) {
  if (!hours) return 'WEEK_DAYS';
  const DAY_WEEKS = ACTIVITY_AVAILABLE_DAY_WEEKS;
  if (
    DAY_WEEKS.split(',').some(
      day =>
        hours.toUpperCase().includes(day) &&
        !hours.startsWith('BEGIN:VCALENDAR')
    )
  ) {
    return 'WEEK_DAYS';
  }
  return 'CALENDAR';
}

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-unused-modules
export function updateActivityTags(tagName, tagValue, tags) {
  const updatedTags = tags ? tags.split(', ') : [];
  if (tagValue === 'TRUE') updatedTags.push(tagName);
  return updatedTags.join(', ');
}

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-unused-modules, @typescript-eslint/no-unused-vars
export async function mapFromBulkActivityToActivity(bulkActivity) {
  const mappedActivity = {
    title: bulkActivity.title,
    description: bulkActivity.description,
    vendorId: bulkActivity.vendorId,
    tags: bulkActivity.tags,
    popularity: bulkActivity.popularity,
    city: bulkActivity.city,
    hours: bulkActivity.hours,
    cancellationPolicy: CANCELLATION_POLICY_OPTIONS.get(
      bulkActivity.cancellationPolicy || ''
    ),
    bachTip: bulkActivity.bachTip,
    faqContent: bulkActivity.faqContent,
    deadline: bulkActivity.deadline,
    commission: bulkActivity.commission,
    categoryId: bulkActivity.category,
    subCategoryId: bulkActivity.subCategory,
    vendorDisplayName: bulkActivity.vendorDisplayName,
  };
  let updatedTags = updateActivityTags(
    'bookable',
    bulkActivity.bookable,
    bulkActivity.tags
  );
  updatedTags = updateActivityTags('hidden', bulkActivity.hidden, updatedTags);
  updatedTags = updateActivityTags(
    'featured',
    bulkActivity.featured,
    updatedTags
  );
  updatedTags = updateActivityTags(
    'bachified',
    bulkActivity.bachified,
    updatedTags
  );
  mappedActivity.tags = updatedTags;
  return mappedActivity;
}

function getSubType(subType) {
  if (!subType) {
    return 'bookingOption';
  }
  if (subType.includes('booking')) {
    return 'bookingOption';
  }
  return subType.toLowerCase();
}

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-unused-modules
export async function mapFromBulkOptionsToBookingOptions(options) {
  const mappedOptions = options.map(async o => {
    const additionalCharges: AdditionalCharges[] = [];
    additionalCharges.push({
      name: 'Vendor Fees',
      percent: o.bookingOptionVendorFees || '0',
    });
    additionalCharges.push({
      name: 'Gratuity',
      percent: o.bookingOptionGratuity || '0',
    });
    const subType = getSubType(o.bookingOptionSubtype);

    let option = {
      additionalCharges,
      attendeesMaximum: o.bookingOptionAttendeesMaximum,
      attendeesMinimum: o.bookingOptionAttendeesMinimum,
      attendeesRequired: o.bookingOptionAttendeesRequired,
      bookingNotes: o.bookingOptionBookingNotes,
      description: o.bookingOptionDescription,
      duration: o.bookingOptionDuration,
      hours: o.bookingOptionHours,
      interval: o.bookingOptionInterval,
      price: o.bookingOptionPrice,
      pricingModel: o.bookingOptionPricingModel,
      title: o.bookingOptionTitle,
    };

    if (subType !== 'undefined') {
      // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
      // @ts-ignore
      option = { ...option, subType };
    }
    if (!!o.taxCode && o.taxCode.length > 0) {
      // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
      // @ts-ignore
      option.taxCode = o.taxCode;
    }
    if (o.locationType) {
      // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
      // @ts-ignore
      option.locationType = o.locationType;
    }

    if (!o.bookingOptionAddress) {
      return option;
    }
    try {
      const geoAddress = await getGeoAddressFromString(o.bookingOptionAddress);
      if (!geoAddress) return option;
      // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
      // @ts-ignore
      option.location = {
        latitude: geoAddress.lat,
        longitude: geoAddress.lng,
        address: geoAddress.address,
      };
      return option;
    } catch (e) {
      const error = e as AxiosError;
      alert(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
      return option;
    }
  });
  const mappedOptionsWithLocations = await Promise.all(mappedOptions);
  return mappedOptionsWithLocations;
}

export async function concatBulkOptionsWithBulkActivity(
  bulkActivity,
  bulkBookingOptions
) {
  const activity = await mapFromBulkActivityToActivity(bulkActivity);
  if (!bulkBookingOptions) return { ...activity, bookingOptions: [] };

  const activityBulkOptions = bulkBookingOptions.filter(
    option => option.productSlug === bulkActivity.productSlug
  );

  const bookingOptions = await mapFromBulkOptionsToBookingOptions(
    activityBulkOptions
  );

  return {
    ...activity,
    bookingOptions,
  };
}

export function parseActivitiesBulkUploadErrors(
  errorString,
  activities,
  bookingOptions
) {
  if (!activities || activities.length === 0) {
    return { activitiesErrors: [], bookingOptionsErrors: [] };
  }
  const arrayOfErrors = errorString.split('\n');
  const activitiesErrors = [];
  const bookingOptionsErrors = [];

  arrayOfErrors.forEach(err => {
    const r = /\d+/g;
    if (bookingOptions && err.match('bookingOptions')) {
      // errors in booking options
      const optionIndex = err.match(r)[1];
      // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
      // @ts-ignore
      bookingOptionsErrors.push(bookingOptions[optionIndex]);
    } else {
      const activityIndex = err.match(r)[0];
      // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
      // @ts-ignore
      activitiesErrors.push(activities[activityIndex]);
    }
  });
  return { activitiesErrors, bookingOptionsErrors };
}

export function mapFromActivityToActivityCSV(activity) {
  const { tags, hours } = activity;
  const formattedHours = hours?.startsWith('BEGIN:VCALENDAR')
    ? 'Google Calendar'
    : hours;
  return {
    ...activity,
    title: escapeQuote(activity.title),
    description: escapeQuote(removeHTML(activity.description)),
    tags: tags.join(', '),
    hours: formattedHours,
  };
}

export function mapFromBookingOptionsToBookingOptionsCSV(activity) {
  const { id, title, bookingOptions } = activity;
  return bookingOptions.map(option => {
    const { hours } = option;
    const formattedHours = hours?.startsWith('BEGIN:VCALENDAR')
      ? 'Google Calendar'
      : hours;
    return {
      ...option,
      activityId: id,
      activityTitle: title,
      hours: formattedHours,
    };
  });
}

export function isActivityTagApplied(tag, tags) {
  return tags.some(tagItem => tagItem === tag);
}

export const getActivitySource = (source: string = 'bach') => {
  return source.toUpperCase();
};

export const isActivityHidden = (activity: Activity) =>
  (activity.tags || []).some(tag => tag.toLowerCase() === 'hidden');
